/**
 * Directory find toolbar — search / Filtry (DirectoryListFilters) + optional
 * Karty/Seznam density toggle (lg+). Screens should prefer this over inventing chrome.
 */

import type { DirectoryDensityMode } from './directoryDensity.js';
import { DirectoryDensityToggle } from './DirectoryDensityToggle.js';
import {
  DirectoryListFilters,
  type DirectoryListFiltersProps,
} from './DirectoryListFilters.js';

export interface DirectoryFindToolbarProps extends DirectoryListFiltersProps {
  /**
   * When true (lg+), density toggle is shown beside filters.
   * Compact: omit — Seznam only; mobile Filtry sheet unchanged.
   */
  readonly showDensityToggle?: boolean;
  /** Required when `showDensityToggle` is true. */
  readonly densityMode?: DirectoryDensityMode;
  /** Required when `showDensityToggle` is true. */
  readonly onDensityChange?: (mode: DirectoryDensityMode) => void;
  /** Optional override for density toggle test id prefix. */
  readonly densityTestId?: string;
  readonly className?: string;
  readonly toolbarTestId?: string;
}

export function DirectoryFindToolbar(props: DirectoryFindToolbarProps): JSX.Element {
  const {
    densityMode,
    onDensityChange,
    showDensityToggle = false,
    densityTestId = 'directory-density-toggle',
    className,
    toolbarTestId = 'directory-find-toolbar',
    ...filtersProps
  } = props;

  const filters = <DirectoryListFilters {...filtersProps} />;

  if (!showDensityToggle) {
    return (
      <div className={className} data-testid={toolbarTestId}>
        {filters}
      </div>
    );
  }

  return (
    <div
      className={[
        'flex flex-col gap-2.5',
        'lg:flex-row lg:items-end lg:gap-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={toolbarTestId}
    >
      <div className="min-w-0 flex-1">{filters}</div>
      <DirectoryDensityToggle
        density={densityMode ?? 'list'}
        onDensityChange={onDensityChange ?? (() => {})}
        visible
        testId={densityTestId}
        className="lg:mb-0"
      />
    </div>
  );
}
